import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Building2, Users, RefreshCw, Activity, ShieldCheck, TrendingUp, AlertTriangle, Copy } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

export default function TrustAdmin() {
  const { user, refreshMe } = useAuth();
  const [mine, setMine] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [series, setSeries] = useState(null);
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(false);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");

  const load = async () => {
    const { data } = await api.get("/trusts/mine");
    setMine(data);
    if (data.admin_trust) {
      const a = await api.get(`/trusts/${data.admin_trust.slug}/analytics`);
      setAnalytics(a.data);
      const ts = await api.get(`/trusts/${data.admin_trust.slug}/timeseries?weeks=12`);
      setSeries(ts.data.weeks || []);
    } else { setAnalytics(null); setSeries(null); }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) return;
    setCreating(true);
    try {
      await api.post("/trusts", { name });
      toast.success("Trust created. You are now an admin.");
      await refreshMe();
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create"); }
    finally { setCreating(false); }
  };

  const join = async () => {
    if (!code.trim()) return;
    setJoining(true);
    try {
      await api.post("/trusts/join", { invite_code: code });
      toast.success("Joined Trust");
      await refreshMe();
      await load();
      setCode("");
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid code"); }
    finally { setJoining(false); }
  };

  const rotate = async () => {
    if (!mine?.admin_trust?.slug) return;
    try {
      const { data } = await api.post(`/trusts/${mine.admin_trust.slug}/rotate-code`);
      toast.success(`New invite code: ${data.invite_code}`);
      await load();
    } catch { toast.error("Could not rotate"); }
  };

  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copied"); };

  const downloadCsv = async () => {
    try {
      const token = localStorage.getItem("sh_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/trusts/${mine.admin_trust.slug}/export.csv?weeks=12`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${mine.admin_trust.slug}-weekly.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch { toast.error("Could not download CSV"); }
  };

  const downloadImpactReport = async () => {
    if (!mine?.admin_trust?.slug) return;
    try {
      toast.loading("Generating Impact Report…", { id: "impact-report" });
      const token = localStorage.getItem("sh_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/trusts/${mine.admin_trust.slug}/impact-report.pdf?weeks=12`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        let detail = res.statusText;
        try { const j = await res.json(); if (j?.detail) detail = j.detail; } catch { /* not JSON */ }
        throw new Error(detail);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `safehaven-impact-${mine.admin_trust.slug}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Impact Report downloaded", { id: "impact-report" });
    } catch (e) {
      toast.error(e.message || "Could not generate Impact Report", { id: "impact-report" });
    }
  };

  if (!mine) return <div className="text-slate-500" data-testid="trust-loading">Loading…</div>;

  // Not yet a member or admin — show create + join
  if (!mine.admin_trust) {
    return (
      <div className="space-y-6 fade-up" data-testid="trust-setup">
        <header>
          <div className="flex items-center gap-2"><Building2 className="w-6 h-6 text-teal-700" /><h1 className="font-display text-3xl font-bold text-slate-900">NHS Trust portal</h1></div>
          <p className="text-slate-600 mt-2 max-w-xl">Trusts use SafeHaven to deploy safety plans across their clinical teams. Create a Trust to become an admin, or join one with an invite code.</p>
        </header>

        {mine.membership && (
          <div className="bg-teal-50 border border-teal-200 rounded-2xl p-4 text-teal-900" data-testid="trust-membership">
            You are a member of <strong>{mine.membership.name}</strong>.
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="trust-create-card">
            <h2 className="font-display text-lg font-bold text-slate-900">Create a Trust</h2>
            <p className="text-sm text-slate-500 mt-1">For NHS Trusts, care providers, supported living services.</p>
            <input data-testid="trust-create-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. South London and Maudsley NHS Trust" className="mt-3 w-full border border-slate-300 rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-teal-500 text-sm" />
            <button data-testid="trust-create-btn" disabled={creating} onClick={create} className="mt-3 w-full bg-teal-700 hover:bg-teal-800 disabled:opacity-60 text-white rounded-xl px-4 py-2.5 font-semibold">
              {creating ? "Creating…" : "Create Trust"}
            </button>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="trust-join-card">
            <h2 className="font-display text-lg font-bold text-slate-900">Join a Trust</h2>
            <p className="text-sm text-slate-500 mt-1">Use an invite code from your Trust admin.</p>
            <input data-testid="trust-join-code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="e.g. ABC12345" className="mt-3 w-full border border-slate-300 rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-teal-500 text-sm font-mono uppercase" />
            <button data-testid="trust-join-btn" disabled={joining} onClick={join} className="mt-3 w-full bg-white border border-slate-300 hover:bg-slate-50 disabled:opacity-60 text-slate-800 rounded-xl px-4 py-2.5 font-semibold">
              {joining ? "Joining…" : "Join Trust"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Admin dashboard
  const stats = analytics || {};
  const StatCard = ({ label, value, icon: Icon, tone, testId }) => (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5" data-testid={testId}>
      <div className={`w-10 h-10 rounded-xl ${tone} flex items-center justify-center mb-3`}><Icon className="w-5 h-5" /></div>
      <div className="text-3xl font-display font-bold text-slate-900">{value ?? "—"}</div>
      <div className="text-sm text-slate-500 mt-1">{label}</div>
    </div>
  );

  return (
    <div className="space-y-6 fade-up" data-testid="trust-admin">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-sm text-slate-500"><ShieldCheck className="w-4 h-4 text-teal-700" /> Trust Admin</div>
          <h1 className="font-display text-3xl font-bold text-slate-900 mt-1">{mine.admin_trust.name}</h1>
          <p className="text-slate-500 mt-1 text-sm">Anonymised aggregates across {stats.total_members ?? 0} members. No identifiable data is shown.</p>
        </div>
        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3 py-2">
          <span className="text-xs text-slate-500">Invite code</span>
          <code className="font-mono font-semibold text-teal-800" data-testid="trust-invite-code">{stats.trust?.invite_code || mine.admin_trust.invite_code}</code>
          <button data-testid="trust-copy-code" onClick={() => copy(stats.trust?.invite_code || mine.admin_trust.invite_code)} className="text-slate-400 hover:text-slate-700"><Copy className="w-4 h-4" /></button>
          <button data-testid="trust-rotate-code" onClick={rotate} className="text-slate-400 hover:text-slate-700"><RefreshCw className="w-4 h-4" /></button>
        </div>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total members" value={stats.total_members} icon={Users} tone="bg-teal-50 text-teal-800" testId="stat-members" />
        <StatCard label="Active (7 days)" value={stats.active_7d} icon={Activity} tone="bg-amber-50 text-amber-800" testId="stat-active" />
        <StatCard label="With a safety plan" value={stats.with_plan_pct != null ? `${stats.with_plan_pct}%` : "—"} icon={ShieldCheck} tone="bg-emerald-50 text-emerald-800" testId="stat-plan" />
        <StatCard label="Avg distress (7d)" value={stats.avg_distress ?? "—"} icon={TrendingUp} tone="bg-rose-50 text-rose-800" testId="stat-distress" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="card-mode-split">
          <h2 className="font-display text-lg font-bold text-slate-900">Mode split</h2>
          <div className="mt-3 space-y-2">
            <div className="flex items-center justify-between"><span className="text-sm text-slate-600">Community</span><span className="font-semibold text-slate-900">{stats.mode_split?.community ?? 0}</span></div>
            <div className="flex items-center justify-between"><span className="text-sm text-slate-600">Inpatient</span><span className="font-semibold text-slate-900">{stats.mode_split?.inpatient ?? 0}</span></div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="card-alerts">
          <h2 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-600" /> Deterioration alerts (7d)</h2>
          <div className="font-display text-4xl font-bold text-slate-900 mt-3">{stats.deterioration_alerts ?? 0}</div>
          <p className="text-xs text-slate-500 mt-1">Number of automated "checking in" nudges fired by the deterioration sweep across your Trust.</p>
        </div>
      </div>

      {series && series.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="trust-timeseries-card">
          <h2 className="font-display text-lg font-bold text-slate-900">12-week trend (anonymised aggregates)</h2>
          <div className="mt-3" style={{ minWidth: 0 }}>
            <ResponsiveContainer width="100%" height={260} debounce={50}>
              <LineChart data={series} margin={{ top: 4, right: 10, bottom: 0, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="week_start" tick={{ fontSize: 11, fill: "#64748b" }} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="active_users" stroke="#0f766e" strokeWidth={2} dot={false} name="Active users" />
                <Line type="monotone" dataKey="checkins" stroke="#d97706" strokeWidth={2} dot={false} name="Check-ins" />
                <Line type="monotone" dataKey="deterioration_alerts" stroke="#dc2626" strokeWidth={2} dot={false} name="Deterioration alerts" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="card-top-signs-v2">
        <h2 className="font-display text-lg font-bold text-slate-900">Top warning signs across the Trust</h2>
        <p className="text-xs text-slate-500 mt-1 mb-3">De-identified term frequency. Useful for planning psychoeducation or staff training.</p>
        {(stats.top_warning_signs || []).length === 0 ? (
          <div className="text-sm text-slate-500">Not enough data yet.</div>
        ) : (
          <ul className="space-y-2">
            {(stats.top_warning_signs || []).map((s) => (
              <li key={s.term} className="flex items-center justify-between bg-slate-50 rounded-xl px-3 py-2">
                <span className="text-sm text-slate-800">{s.term}</span>
                <span className="text-xs font-semibold text-teal-800 bg-teal-50 px-2 py-0.5 rounded-full">{s.count} mentions</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-900 flex flex-wrap items-center justify-between gap-3">
        <span>All figures are anonymised aggregates. Individual users, plans, journal entries and chats are NEVER visible to Trust admins.</span>
        <div className="flex flex-wrap gap-2">
          <button data-testid="trust-impact-report" onClick={downloadImpactReport} className="bg-slate-900 hover:bg-slate-800 text-white rounded-xl px-4 py-2 text-sm font-semibold">
            Download Impact Report PDF
          </button>
          <button data-testid="trust-csv-download" onClick={downloadCsv} className="bg-teal-700 hover:bg-teal-800 text-white rounded-xl px-4 py-2 text-sm font-semibold">
            Download CSV (12 weeks)
          </button>
        </div>
      </div>
    </div>
  );
}
